'use client';

import {
  Avatar,
  Button,
  ChatBubble,
  ChatThread,
  Dropdown,
  File,
  Flex,
  Text,
} from '@/_components';
import type { ResultListItem } from '@/_components';
import { downloadAttachment } from '@/_api/client-documents';
import Link from 'next/link';
import { DOCUMENT_ANSWER_PREFIX } from '@/_constants/documents';
import { useContext, type FC, useMemo } from 'react';
import { getStillEditable } from '@/_utils/message';
import { UserContext } from '@/_contexts/UserProvider';
import { AdminConversationsContext } from '@/_contexts/admin/AdminConversationsProvider';
import { QUESTION_SEPARATOR } from '@/_constants/questions';
import type { Message } from '@/_types/models/conversations';
import type { AdminChatMessageProps } from './AdminChatMessage.types';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { processMessageText } from '@/_utils/strings';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import { ScheduledPill } from './components/ScheduledPill/ScheduledPill';

export const AdminChatMessage: FC<AdminChatMessageProps> = ({
  message,
  isReply,
  isPreview,
  isActive,
  onSelectReply,
  unreadThreadMessageCount,
  isMe,
  onEditMessage,
  onEditScheduleTime,
  onSendAIResponse,
  onDeleteMessage,
  currentTime,
}) => {
  const { setPreviewFile } = useContext(PreviewContext);

  const { selectedMessage, reloadMessages } = useContext(
    AdminConversationsContext,
  );
  const { previewConversation } = useContext(UserPreviewContext);
  const { token } = useContext(UserContext);
  const { selectedCompany } = useContext(CompanyContext);

  const isAttached: boolean = message?.text?.startsWith?.(
    DOCUMENT_ANSWER_PREFIX,
  );

  const attachments: Array<string> = useMemo(() => {
    return isAttached
      ? message?.text
          .replace(DOCUMENT_ANSWER_PREFIX, '')
          .split(QUESTION_SEPARATOR)
          .filter((attachment) => attachment.trim() !== '')
      : [];
  }, [isAttached, message?.text]);

  const threadLevelMessages: Array<Message> = useMemo(() => {
    const messages: Array<Message> = isPreview
      ? previewConversation?.messages || []
      : selectedMessage?.messages || [];

    return (
      messages?.filter?.((m: any) => m.message_thread_id === message.id) || []
    );
  }, [selectedMessage, previewConversation, message, isPreview]);

  const lastReplyDate: string = useMemo(() => {
    let latest = new Date();

    if (message.created_at) latest = new Date(message.created_at);

    const hasThreads = threadLevelMessages?.length;

    if (hasThreads) {
      threadLevelMessages?.forEach((thread) => {
        const threadCreatedAt = new Date(thread.created_at);
        if (threadCreatedAt > latest) {
          latest = threadCreatedAt;
        }
      });
    }

    return latest?.toISOString?.();
  }, [threadLevelMessages, message?.created_at]);

  const onOpenThread = () => {
    onSelectReply?.(message);
    reloadMessages(true); // ensures unread message count is immediately updated
  };

  const participants: Array<string> = useMemo(() => {
    const uniqueNamesSet: Set<string> = new Set(
      threadLevelMessages?.map((message) => message.user_name),
    );
    const uniqueNamesArray: Array<string> =
      Array.from(uniqueNamesSet).filter(Boolean);

    return uniqueNamesArray;
  }, [threadLevelMessages]);

  const documentsLink: string = useMemo(
    () => `/admin/${selectedCompany?.hash_id}/documents?path=%2FAttachments`,
    [selectedCompany?.hash_id],
  );

  const messageDeleted: boolean = message?.text === 'This message was deleted';

  const isStillEditable: boolean = useMemo(() => {
    if (!currentTime) return false;

    return getStillEditable(message, currentTime);
  }, [message, currentTime]);

  const dropdownItems: Array<ResultListItem> = useMemo(() => {
    const items: Array<ResultListItem> = [];

    if (message?.scheduled) {
      items.push({
        name: 'Send Draft',
        type: 'item',
        onClick: () => onSendAIResponse?.(message),
      });
    }

    if (!isAttached) {
      items.push({
        name: 'Edit',
        type: 'item',
        onClick: () => onEditMessage?.(message),
      });
    }

    if (message?.scheduled && message?.scheduled_time) {
      items.push({
        name: 'Change Schedule Time',
        type: 'item',
        onClick: () => onEditScheduleTime?.(message),
      });
    }

    items.push({
      name: isStillEditable && !message?.scheduled ? 'Unsend' : 'Delete',
      type: 'item',
      onClick: () => onDeleteMessage?.(message),
    });

    return items;
  }, [
    message,
    isAttached,
    isStillEditable,
    onDeleteMessage,
    onSendAIResponse,
    onEditMessage,
    onEditScheduleTime,
  ]);

  const smsDeliveryStatus: React.ReactNode = useMemo(() => {
    const text =
      message.sms_delivery_status === 'pending'
        ? 'SMS Pending'
        : message.sms_delivery_status === 'delivered'
          ? 'SMS Delivered'
          : message.sms_delivery_status === 'failed'
            ? 'SMS Failed'
            : '';
    return (
      <Text type="tiny" color="secondary">
        {text}
      </Text>
    );
  }, [message.sms_delivery_status]);

  return (
    <ChatBubble
      id={message.id}
      date={message.created_at}
      unreadThreadMessageCount={unreadThreadMessageCount}
      isMe={isMe}
      isActive={isActive}
      isScheduled={!!message?.scheduled}
      isAIResponse={!!message?.scheduled && !message?.scheduled_time}
      isReply={isReply}
      onOpenThread={onOpenThread}
      avatarName={message.user_name}
      isEdited={message?.is_edited}
      tag={message.tag || 'general'}
    >
      {isAttached && (
        <Flex isColumn={true} gap={4}>
          <Flex isColumn={true} gap={1}>
            {attachments.map((attachment: string, key) => {
              // NOTE @chris this is to handle invalid attached in Anomaly (38)
              // https://new-internal.soraban.com/admin/T9OyliahXqn0BVn/messages/87995
              const name: string = attachment
                ? attachment?.substring(attachment?.lastIndexOf?.('/') + 1)
                : 'Unnamed';

              const onPreview = async () => {
                const { document } = await downloadAttachment({
                  link: attachment,
                  token: token!,
                });

                setPreviewFile(document?.data?.link);
              };

              return (
                <File title={name} key={key} shouldEllipse={false}>
                  <Button
                    intent="ghost"
                    onClick={onPreview}
                    size="tiny"
                    value="Preview"
                  ></Button>
                </File>
              );
            })}
          </Flex>
          <Text type="tiny" color="secondary">
            Attachment can be found under{' '}
            <Link href={documentsLink}>Documents/Attachments</Link>.
          </Text>
        </Flex>
      )}
      {messageDeleted ? (
        <Text type="small" color="secondary">
          This message was deleted
        </Text>
      ) : isAttached ? null : (
        <div
          dangerouslySetInnerHTML={{
            __html: processMessageText(message.text),
          }}
        />
      )}
      {!isMe && message?.scheduled && (
        <ScheduledPill
          timestamp={message.scheduled_time}
          isPreview={isPreview}
        />
      )}
      {!isMe && message.sms_delivery_status === 'received' && (
        <Text type="tiny" color="secondary">
          Sent From SMS
        </Text>
      )}
      {isMe && !messageDeleted && (
        <Flex isColumn={true} gap={4}>
          {message?.scheduled && (
            <ScheduledPill timestamp={message.scheduled_time} />
          )}
          <Flex isBetween={true} isYCentered={true}>
            <Flex xAlign="start">{smsDeliveryStatus}</Flex>
            <Flex xAlign="end">
              <Dropdown
                items={dropdownItems}
                onSelect={(item) => item.onClick?.(item)}
                placement="bottom-end"
              >
                <Button
                  iconRight="dots-three"
                  size="tiny"
                  isIconOnly={true}
                  intent="ghost"
                  tooltip="Actions"
                  withTooltip={true}
                  value="Actions"
                />
              </Dropdown>
            </Flex>
          </Flex>
        </Flex>
      )}
      {!isActive && !isReply && threadLevelMessages?.length > 0 && (
        <ChatThread
          onClick={onOpenThread}
          lastReplyDate={lastReplyDate}
          replyCount={threadLevelMessages?.length ?? 0}
          participantCount={participants?.length}
        >
          {participants?.map((participant, key) => {
            return (
              <Avatar key={key} type="user" name={participant} size={24} />
            );
          })}
        </ChatThread>
      )}
    </ChatBubble>
  );
};
